/**
 * Shared plumbing for the globe layer modules: the viewer, the mount options,
 * the status channel and a few Cesium helpers every layer uses.
 */
import * as Cesium from 'cesium';
import type { GlobeOptions, GlobeStatusLevel } from './GlobeEngine';

export interface GlobeContext {
  readonly viewer: Cesium.Viewer;
  readonly options: GlobeOptions;
  /** Report a human-readable status (toasts in the app). Warnings and errors are also logged. */
  status(level: GlobeStatusLevel, text: string): void;
  /** Current simulation time. */
  now(): Cesium.JulianDate;
}

/** Parse a CSS colour, falling back when the string is missing or invalid. */
export function cssColor(css: string | undefined, fallback: string): Cesium.Color {
  const parsed = css ? Cesium.Color.fromCssColorString(css) : undefined;
  return parsed ?? Cesium.Color.fromCssColorString(fallback);
}

/** Label fonts: the app's own faces (loaded by main.tsx through fontsource), replacing the
 *  original Big Shoulders and Overpass which were never bundled. Display labels are semibold. */
export const FONT = {
  bigShoulders: (px: number, weight = '600') => `${weight ? weight + ' ' : ''}${px}px 'Inter Variable', Inter, sans-serif`,
  overpass: (px: number) => `500 ${px}px 'Inter Variable', Inter, sans-serif`,
  mono: (px: number) => `${px}px 'JetBrains Mono Variable', ui-monospace, monospace`,
};

export function julianFromMs(ms: number): Cesium.JulianDate {
  return Cesium.JulianDate.fromDate(new Date(ms));
}

export function msFromJulian(time: Cesium.JulianDate): number {
  return Cesium.JulianDate.toDate(time).getTime();
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Remove an entity by id when it exists. */
export function removeEntity(viewer: Cesium.Viewer, id: string): boolean {
  const entity = viewer.entities.getById(id);
  if (!entity) return false;
  return viewer.entities.remove(entity);
}

/** Convert a position property's value at a time to {lat, lon, altKm}, or null when undefined. */
export function toGeodetic(position: Cesium.Cartesian3 | undefined): { lat: number; lon: number; altKm: number } | null {
  if (!position) return null;
  const carto = Cesium.Cartographic.fromCartesian(position);
  if (!carto) return null;
  return {
    lat: Cesium.Math.toDegrees(carto.latitude),
    lon: Cesium.Math.toDegrees(carto.longitude),
    altKm: carto.height / 1000,
  };
}
